/**
 * Hisobotlar 2-bosqich (docs/12-moliya.md): erkin davr, tepadagi ko'rsatkichlar (oldingi davr bilan), Trendlar va
 * Moliya tablari, Excel eksport. Hamma grafik ranglari -- tokenlardan.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowUpRight, FileSpreadsheet } from "lucide-react";
import { buildQuery } from "@/lib/api";
import { cn, dayjs } from "@/lib/utils";
import { currentPeriod, currentQuarter, periodKind, periodLabel, periodOptions, previousPeriod, quarterOptions, useReport, yearOptions, type PeriodKind } from "@/features/dashboards/api";
import type { FinanceBlock, ReportSection, SummaryBlock } from "@/features/dashboards/types";
import { fmt, num } from "@/features/finance/money";
import { stageMonthLink, type StageEvent } from "@/features/drivers/stageMonth";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { InfoTip } from "@/components/ui/info-tip";
import { Input, Select } from "@/components/ui/field";
import { ErrorState, PageLoading } from "@/components/ui/misc";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export type ReportQuery = { period: string; branch?: number; recruiter?: number };

/**
 * Hisobot raqami ortidagi driverlar ro'yxati (faqat oy davrida -- Drivers filtrlari oy bo'yicha): o'sha bosqich va oy, tanlangan
 * filial va recruiter bilan. Chorak/yil/oraliqda -- undefined (raqam bosilmaydi).
 */
export function reportDriversLink(query: ReportQuery, event: StageEvent): string | undefined {
  if (!/^\d{4}-\d{2}$/.test(query.period)) return undefined;
  return stageMonthLink(event, query.period, { branch: query.branch, owner: query.recruiter });
}

const AXIS = { fontSize: 11, fill: "var(--color-muted)" };
const TOOLTIP = { fontSize: 12, borderRadius: 12, borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-ink)" };
const PALETTE = ["var(--color-chart)", "var(--color-accent)", "var(--color-success)", "var(--color-primary)", "var(--color-danger)", "var(--color-muted)"];
const monthLabel = (p: string) => dayjs(`${p}-01`).format("MMM YY");
const shortUsd = (v: number) => (Math.abs(v) >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`);

// ---- Davr tanlash ----

const KINDS: PeriodKind[] = ["month", "quarter", "year", "range"];

export function PeriodPicker({ value, onChange }: { value: string; onChange: (period: string) => void }) {
  const { t } = useTranslation("reports");
  const kind = periodKind(value);
  const [from, to] = kind === "range" ? value.split("_") : [dayjs().startOf("month").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")];
  const [range, setRange] = useState({ from, to });
  useEffect(() => setRange({ from, to }), [from, to]);

  function setKind(k: PeriodKind) {
    if (k === "month") onChange(currentPeriod());
    else if (k === "quarter") onChange(currentQuarter());
    else if (k === "year") onChange(String(dayjs().year()));
    else onChange(`${range.from}_${range.to}`);
  }

  function setRangePart(part: "from" | "to", v: string) {
    const next = { ...range, [part]: v };
    setRange(next);
    if (next.from && next.to && next.from <= next.to) onChange(`${next.from}_${next.to}`);
  }

  const options = kind === "month" ? periodOptions(24) : kind === "quarter" ? quarterOptions(8) : kind === "year" ? yearOptions(4) : [];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={kind} onChange={(e) => setKind(e.target.value as PeriodKind)} className="w-auto" aria-label={t("periodPicker.kind")}>
        {KINDS.map((k) => (
          <option key={k} value={k}>
            {t(`periodPicker.${k}`)}
          </option>
        ))}
      </Select>
      {kind === "range" ? (
        <span className="flex items-center gap-1">
          <Input type="date" value={range.from} max={range.to} onChange={(e) => setRangePart("from", e.target.value)} className="w-[150px]" aria-label={t("periodPicker.from")} />
          –
          <Input type="date" value={range.to} min={range.from} max={dayjs().format("YYYY-MM-DD")} onChange={(e) => setRangePart("to", e.target.value)} className="w-[150px]" aria-label={t("periodPicker.to")} />
        </span>
      ) : (
        <Select value={value} onChange={(e) => onChange(e.target.value)} className="w-auto min-w-[150px]" aria-label={t("periodPicker.value")}>
          {!options.some((o) => o.value === value) && <option value={value}>{periodLabel(value)}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}

// ---- Excel ----

export function exportUrl(section: ReportSection, query: ReportQuery): string {
  return `/api/reports/${section}/export/${buildQuery(query)}`;
}

export function ExportButton({ section, query }: { section: ReportSection; query: ReportQuery }) {
  const { t } = useTranslation("reports");
  return (
    <a
      href={exportUrl(section, query)}
      className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full bg-fill px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-fill-hover"
      title={t("export.title")}
    >
      <FileSpreadsheet size={14} /> {t("export.button")}
    </a>
  );
}

// ---- O'zgarish (oldingi davrga nisbatan) ----

/** "+12%" yashil yoki qizil; goodWhenDown -- kamaygani yaxshi (masalan ishga olishgacha kun, yo'qotish, xarajat) */
export function Delta({ cur, prev, goodWhenDown, kind = "pct" }: { cur: number | null; prev: number | null; goodWhenDown?: boolean; kind?: "pct" | "pp" }) {
  const { t } = useTranslation("reports");
  if (cur === null || prev === null) return <span className="text-muted">{t("delta.none")}</span>;
  if (kind === "pp") {
    const d = Math.round((cur - prev) * 10) / 10;
    if (d === 0) return <span className="text-muted">{t("delta.same")}</span>;
    const good = goodWhenDown ? d < 0 : d > 0;
    return <span className={good ? "text-success" : "text-danger"}>{t("delta.pp", { value: d > 0 ? `+${d}` : d })}</span>;
  }
  if (!prev) return <span className="text-muted">{cur ? t("delta.new") : t("delta.same")}</span>;
  const d = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  if (d === 0) return <span className="text-muted">{t("delta.same")}</span>;
  const good = goodWhenDown ? d < 0 : d > 0;
  return <span className={good ? "text-success" : "text-danger"}>{`${d > 0 ? "▲ +" : "▼ "}${d}%`}</span>;
}

function Stat({ label, value, delta, info, to }: { label: string; value: React.ReactNode; delta: React.ReactNode; info?: React.ReactNode; to?: string }) {
  const card = (
    <Card className={cn("relative px-4 py-3", to && "transition-[border-color,box-shadow] duration-150 group-hover:border-border group-hover:shadow-sm")}>
      {to && <ArrowUpRight size={14} strokeWidth={1.75} aria-hidden className="absolute top-3 right-3 text-faint transition-colors group-hover:text-ink" />}
      <div className={cn("text-[12px] text-ink-soft", to && "pr-5")}>
        {label}
        {info && <InfoTip text={info} size={12} className="ml-1.5" />}
      </div>
      <div className="mt-1 text-[24px] leading-tight font-bold">{value}</div>
      <div className="mt-1 text-[11px]">{delta}</div>
    </Card>
  );
  // Raqam ortidagi ro'yxat (foydalanuvchi qoidasi 2026-10-18); davr oy bo'lsa
  return to ? (
    <Link to={to} className="group block rounded-card focus-visible:outline-none">
      {card}
    </Link>
  ) : (
    card
  );
}

/** Izoh: ta'rif (metrics) va ostida xira qo'shimcha qator (taqqoslash davri, hire soni) */
function tip(main: string, extra?: string) {
  return (
    <>
      {main}
      {extra && <span className="mt-1 block opacity-70">{extra}</span>}
    </>
  );
}

// ---- Tepadagi ko'rsatkichlar ----

export function SummaryStrip({ query }: { query: ReportQuery }) {
  const { t } = useTranslation("reports");
  const { t: ti } = useTranslation("metrics");
  const report = useReport("summary", query);
  if (!report.data) return null;
  const c: SummaryBlock = report.data.current;
  const p: SummaryBlock = report.data.previous;
  const vs = t("delta.vs", { period: periodLabel(previousPeriod(query.period)) });
  const tipVs = (key: string) => tip(ti(key), vs);
  // Oy davri bo'lsa plitka ro'yxatni ochadi: shu oyda qo'shilgan / yuborilgan / ishga olingan / yo'qotilganlar (filtrlar bilan)
  const listLink = (event: StageEvent) => reportDriversLink(query, event);
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" title={vs}>
      <Stat to={listLink("new")} label={t("summary.new")} value={c.new} delta={<Delta cur={c.new} prev={p.new} />} info={tipVs("newDrivers")} />
      <Stat to={listLink("submitted")} label={t("summary.submitted")} value={c.submitted} delta={<Delta cur={c.submitted} prev={p.submitted} />} info={tipVs("submitted")} />
      <Stat to={listLink("hired")} label={t("summary.hired")} value={c.hired} delta={<Delta cur={c.hired} prev={p.hired} />} info={tipVs("hires")} />
      <Stat label={t("summary.conversion")} value={c.conversion !== null ? `${c.conversion}%` : "—"} delta={<Delta cur={c.conversion} prev={p.conversion} kind="pp" />} info={tipVs("conversion")} />
      <Stat label={t("summary.timeToHire")} value={c.time_to_hire !== null ? t("summary.days", { n: c.time_to_hire }) : "—"} delta={<Delta cur={c.time_to_hire} prev={p.time_to_hire} goodWhenDown />} info={tipVs("avgTime")} />
      <Stat to={listLink("lost")} label={t("summary.lost")} value={c.lost} delta={<Delta cur={c.lost} prev={p.lost} goodWhenDown />} info={tipVs("lost")} />
    </div>
  );
}

// ---- Trendlar ----

export function TrendsTab({ query }: { query: ReportQuery }) {
  const { t } = useTranslation("reports");
  const { t: tc } = useTranslation();
  const report = useReport("trends", query);
  if (report.isPending) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  const months = report.data.months.map((m) => ({ ...m, label: monthLabel(m.period) }));
  const src = report.data.sources;
  const srcRows = src.rows.map((r) => ({ ...r, label: monthLabel(String(r.period)) }));
  const srcName = (k: string) => (k === "other" ? t("trends.otherSources") : tc(`source.${k}`, { defaultValue: k }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t("trends.hiresTitle")} subtitle={t("trends.hiresSub")} />
          <CardBody>
            <div className="h-[260px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <XAxis dataKey="label" tick={AXIS} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                  <YAxis yAxisId="n" tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                  <YAxis yAxisId="pct" orientation="right" tick={AXIS} axisLine={false} tickLine={false} unit="%" width={40} />
                  <Tooltip cursor={{ fill: "var(--color-sidebar)" }} contentStyle={TOOLTIP} formatter={(v, name) => [name === "conversion" ? `${v ?? "—"}%` : v, t(`trends.series.${String(name)}`)]} />
                  <Legend formatter={(name) => <span className="text-[12px] text-ink-soft">{t(`trends.series.${String(name)}`)}</span>} iconSize={10} />
                  <Bar yAxisId="n" dataKey="new" fill="var(--color-chart)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  <Bar yAxisId="n" dataKey="hired" fill="var(--color-primary)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  <Line yAxisId="pct" dataKey="conversion" type="monotone" stroke="var(--color-budget)" strokeWidth={2} dot={{ r: 2 }} connectNulls isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t("trends.sourcesTitle")} subtitle={t("trends.sourcesSub")} />
          <CardBody>
            {src.keys.length === 0 ? (
              <p className="py-10 text-center text-[13px] text-muted">{t("trends.empty")}</p>
            ) : (
              <div className="h-[260px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={srcRows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="label" tick={AXIS} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                    <YAxis tick={AXIS} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                    <Tooltip cursor={{ fill: "var(--color-sidebar)" }} contentStyle={TOOLTIP} formatter={(v, name) => [v, srcName(String(name))]} />
                    <Legend formatter={(name) => <span className="text-[12px] text-ink-soft">{srcName(String(name))}</span>} iconSize={10} />
                    {src.keys.map((k, i) => (
                      <Bar key={k} dataKey={k} stackId="s" fill={PALETTE[i % PALETTE.length]} isAnimationActive={false} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
      <Card>
        <CardHeader title={t("trends.tableTitle")} />
        <CardBody className="px-0 pb-0">
          <Table variant="flush">
            <thead>
              <tr>
                <Th>{t("trends.col.month")}</Th>
                <Th className="text-right">{t("trends.series.new")}</Th>
                <Th className="text-right">{t("trends.col.contacted")}</Th>
                <Th className="text-right">{t("trends.col.submitted")}</Th>
                <Th className="text-right">{t("trends.series.hired")}</Th>
                <Th className="text-right">{t("trends.col.lost")}</Th>
                <Th className="text-right">{t("trends.series.conversion")}</Th>
              </tr>
            </thead>
            <tbody>
              {[...months].reverse().map((m) => (
                <Tr key={m.period}>
                  <Td className="font-medium">{dayjs(`${m.period}-01`).format("MMMM YYYY")}</Td>
                  <Td className="text-right">{m.new}</Td>
                  <Td className="text-right">{m.contacted}</Td>
                  <Td className="text-right">{m.submitted}</Td>
                  <Td className="text-right font-semibold">{m.hired}</Td>
                  <Td className="text-right text-ink-soft">{m.lost}</Td>
                  <Td className="text-right">{m.conversion !== null ? `${m.conversion}%` : "—"}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}

// ---- Moliya ----

export function FinanceTab({ query, narrowed }: { query: ReportQuery; narrowed: boolean }) {
  const { t } = useTranslation("reports");
  const { t: ti } = useTranslation("metrics");
  const report = useReport("finance", query);
  if (report.isPending) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  const { current: c, previous: p, carriers, branches, series } = report.data;
  const vs = t("delta.vs", { period: periodLabel(previousPeriod(query.period)) });
  const money = (b: FinanceBlock, k: keyof FinanceBlock) => num(b[k] as string | number | null);
  const chart = series.map((s) => ({ label: monthLabel(s.period), expected: num(s.expected), cost: num(s.cost), profit: num(s.profit) }));
  return (
    <div className="space-y-4">
      {(narrowed || c.no_fee_amount > 0) && (
        <div className="rounded-card border border-budget bg-warning-soft px-4 py-2 text-[12px] text-ink-soft">
          {[narrowed ? t("finance.branchOnly") : "", c.no_fee_amount > 0 ? t("finance.noFeeAmount", { count: c.no_fee_amount }) : ""].filter(Boolean).join(" ")}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" title={vs}>
        <Stat label={t("finance.expected")} value={<span className="text-success">{fmt(c.expected)}</span>} delta={<Delta cur={money(c, "expected")} prev={money(p, "expected")} />} info={tip(ti("expected"), vs)} />
        <Stat label={t("finance.received")} value={fmt(c.received)} delta={<Delta cur={money(c, "received")} prev={money(p, "received")} />} info={tip(ti("received"), vs)} />
        <Stat label={t("finance.cost")} value={fmt(c.cost)} delta={<Delta cur={money(c, "cost")} prev={money(p, "cost")} goodWhenDown />} info={tip(ti("cost"), vs)} />
        <Stat label={t("finance.profit")} value={<span className={money(c, "profit") < 0 ? "text-danger" : "text-success"}>{fmt(c.profit)}</span>} delta={<Delta cur={money(c, "profit")} prev={money(p, "profit")} />} info={tip(ti("profit"), vs)} />
        <Stat label={t("finance.costPerHire")} value={c.cost_per_hire !== null ? fmt(c.cost_per_hire) : "—"} delta={<Delta cur={c.cost_per_hire} prev={p.cost_per_hire} goodWhenDown />} info={tip(ti("costPerHire"), t("finance.hiresN", { count: c.hires }))} />
        <Stat label={t("finance.revenuePerHire")} value={c.revenue_per_hire !== null ? fmt(c.revenue_per_hire) : "—"} delta={<Delta cur={c.revenue_per_hire} prev={p.revenue_per_hire} />} info={tip(ti("revenuePerHire"), t("finance.hiresN", { count: c.hires }))} />
      </div>

      <Card>
        <CardHeader title={t("finance.chartTitle")} subtitle={t("finance.chartSub")} />
        <CardBody>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={AXIS} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} tickFormatter={shortUsd} width={48} />
                <Tooltip cursor={{ fill: "var(--color-sidebar)" }} contentStyle={TOOLTIP} formatter={(v, name) => [fmt(Number(v)), t(`finance.series.${String(name)}`)]} />
                <Legend formatter={(name) => <span className="text-[12px] text-ink-soft">{t(`finance.series.${String(name)}`)}</span>} iconSize={10} />
                <Bar dataKey="expected" fill="var(--color-success)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="cost" fill="var(--color-budget)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Line dataKey="profit" type="monotone" stroke="var(--color-primary)" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t("finance.carriersTitle")} subtitle={t("finance.carriersSub")} />
          <CardBody className="px-0 pb-0">
            {carriers.length === 0 ? (
              <p className="px-5 pb-5 text-[13px] text-muted">{t("finance.empty")}</p>
            ) : (
              <Table variant="flush">
                <thead>
                  <tr>
                    <Th>{t("finance.col.carrier")}</Th>
                    <Th className="text-right">{t("finance.col.hires")}</Th>
                    <Th className="text-right">{t("finance.col.expected")}</Th>
                    <Th className="text-right">{t("finance.col.received")}</Th>
                    <Th className="text-right">{t("finance.col.outstanding")}</Th>
                    <Th>{t("finance.col.status")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {carriers.map((r) => (
                    <Tr key={r.carrier_id}>
                      <Td className="font-medium">{r.carrier}</Td>
                      <Td className="text-right">{r.hires}</Td>
                      <Td className="text-right">{fmt(r.expected)}</Td>
                      <Td className="text-right text-success">{fmt(r.received)}</Td>
                      <Td className={cn("text-right", num(r.outstanding) > 0 && "font-semibold text-danger")}>{fmt(r.outstanding)}</Td>
                      <Td className="space-x-1 whitespace-nowrap">
                        {r.paid > 0 && <Badge tone="green">{t("finance.paidN", { count: r.paid })}</Badge>}
                        {r.pending > 0 && <Badge tone="grey">{t("finance.pendingN", { count: r.pending })}</Badge>}
                        {r.not_paid > 0 && <Badge tone="red">{t("finance.notPaidN", { count: r.not_paid })}</Badge>}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t("finance.branchesTitle")} subtitle={t("finance.branchesSub")} />
          <CardBody className="px-0 pb-0">
            <Table variant="flush">
              <thead>
                <tr>
                  <Th>{t("finance.col.branch")}</Th>
                  <Th className="text-right">{t("finance.col.hires")}</Th>
                  <Th className="text-right">{t("finance.col.cost")}</Th>
                  <Th className="text-right">{t("finance.col.expected")}</Th>
                  <Th className="text-right">{t("finance.col.profit")}</Th>
                  <Th className="text-right">{t("finance.col.costPerHire")}</Th>
                </tr>
              </thead>
              <tbody>
                {branches.map((r) => (
                  <Tr key={r.branch_id}>
                    <Td className="font-medium">{r.branch}</Td>
                    <Td className="text-right">{r.hires}</Td>
                    <Td className="text-right">{fmt(r.cost)}</Td>
                    <Td className="text-right">{fmt(r.expected)}</Td>
                    <Td className={cn("text-right font-semibold", num(r.profit) < 0 ? "text-danger" : "text-success")}>{fmt(r.profit)}</Td>
                    <Td className="text-right">{r.cost_per_hire !== null ? fmt(r.cost_per_hire) : "—"}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
